import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import FilterDropdown from "../../components/FilterDropdown";

import { NumericFilterEditor } from "./NumericFilterEditor";

const QUICK_VALUES = [10, 25, 50, 100] as const;

export function MinOverpayControl({
  value,
  onChange,
  insufficientReferences = false,
}: {
  value: number | null;
  insufficientReferences?: boolean;
  onChange: (next: number | null) => void;
}) {
  const isQuickValue =
    value !== null && (QUICK_VALUES as readonly number[]).includes(value);
  const selectedValue = value ?? 0;
  const [customValue, setCustomValue] = useState(
    value !== null && !isQuickValue ? String(value) : "",
  );
  const [customOpen, setCustomOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (value !== null && !isQuickValue) setCustomValue(String(value));
  }, [isQuickValue, value, insufficientReferences]);

  const options = useMemo(() => {
    const items = [
      { value: "0", label: insufficientReferences ? "Alle Unterschiede" : "Alle Mehrkosten" },
      ...QUICK_VALUES.map((quick) => ({
        value: String(quick),
        label: `≥ ${quick.toLocaleString("de-AT")} in Belegwährung`,
      })),
    ];
    if (value !== null && !isQuickValue) {
      items.push({
        value: String(value),
        label: `≥ ${value.toLocaleString("de-AT")} in Belegwährung`,
      });
    }
    items.push({ value: "custom", label: "Eigener Wert …" });
    return items;
  }, [isQuickValue, value, insufficientReferences]);

  const closeEditor = useCallback((restoreFocus: boolean) => {
    setCustomOpen(false);
    if (restoreFocus) {
      window.requestAnimationFrame(() =>
        anchorRef.current?.querySelector("button")?.focus(),
      );
    }
  }, []);

  return (
    <div ref={anchorRef} className="relative">
      <FilterDropdown
        label={insufficientReferences ? "Unterschied" : "Mehrkosten"}
        ariaLabel={insufficientReferences ? "Mindestunterschied filtern" : "Mindestmehrkosten filtern"}
        options={options}
        value={String(selectedValue)}
        defaultValue="0"
        showValue
        onChange={(next) => {
          if (next === "custom") {
            setCustomValue(value !== null && !isQuickValue ? String(value) : "");
            setCustomOpen(true);
            return;
          }
          const parsed = Number(next);
          onChange(parsed === 0 ? null : parsed);
        }}
      />
      <NumericFilterEditor
        open={customOpen}
        title={insufficientReferences ? "Mindestunterschied in Belegwährung" : "Mindestmehrkosten in Belegwährung"}
        ariaLabel="Eigene Mindestmehrkosten festlegen"
        value={customValue}
        onValueChange={setCustomValue}
        onApply={(next) => {
          onChange(next);
          closeEditor(true);
        }}
        onClose={closeEditor}
        isValid={(next) => Number.isFinite(next) && next > 0}
        invalidMessage="Bitte einen Wert größer als 0 eingeben."
        min={0}
        step={5}
        placeholder="z. B. 30"
      />
    </div>
  );
}
